import React, { Component } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box } from 'bloomer/lib/elements/Box';
import { Content } from 'bloomer/lib/elements/Content';
import { Title } from 'bloomer/lib/elements/Title';
import MultiDatePicker from '../elements/MultiDatePicker';
import moment, { Moment } from 'moment';
import { Button } from 'bloomer/lib/elements/Button';
import styled from 'styled-components';
import { Container } from 'bloomer/lib/layout/Container';
import ReportOptions from '../components/reportOptions';
import Conditional from '../components/Conditional';
import { infoAlert, popupErrorFromServer } from '../util/helperFunctions';
import api from '../util/api';
import ReportContent from '../components/ReportContent';

const CenteredForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  & > * {
    margin-bottom: 15px;
  }
`;

interface PropTypes extends RouteComponentProps<{}> {}
interface StateTypes {
  startDate: Moment;
  endDate: Moment;
  focusedInput: 'startDate' | 'endDate' | null;
  step: 1 | 2 | 3;
  exludeTimeMax: number;
  animalType: animalType | '';
  [Key: string]: any;
}

class Report extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    startDate: moment().subtract('30', 'days'),
    endDate: moment(),
    focusedInput: null,
    step: 1,
    exludeTimeMax: 1000,
    animalType: '',
  };

  handleFocusChange = (focusedInput: 'startDate' | 'endDate' | null) => {
    this.setState({ focusedInput });
  };

  handleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => {
    if (!startDate || !endDate) return;
    this.setState({ startDate, endDate, step: 1 });
  };

  handleDatesSelected = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { startDate, endDate } = this.state;
    if (endDate.isBefore(startDate)) {
      return infoAlert('The end date must come after the start date.');
    }
    this.setStep(2);
  };

  setStep = (number: 1 | 2 | 3) => this.setState({ step: number });

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setStep(2);
    if (e.target.type === 'checkbox') {
      this.setState({
        [e.target.name]: e.target.checked,
      });
    } else {
      this.setState({
        [e.target.name]: e.target.value,
      });
    }
  };

  getEnrichmentData = (getFilters: () => any) => async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    try {
      e.preventDefault();
      const { startDate, endDate, animalType, exludeTimeMax } = this.state;
      const filters = getFilters();
      const data = await api.enrichmentData.get({
        exludeTimeMax,
        animalType: animalType ? animalType : undefined,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        filters: JSON.stringify(filters),
      });
      this.setState({
        enrichmentData: data,
        step: 3,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  render() {
    const {
      startDate,
      endDate,
      focusedInput,
      step,
      exludeTimeMax,
      animalType,
      enrichmentData,
    } = this.state;
    return (
      <Container className="report-page">
        <Box className="hide-on-print">
          <Content>
            <Title>Welcome to the Reporting Page:</Title>
            <p>
              This page will allow you to generate a report for a given time
              period.
            </p>
            <p>Please select a date range to continue.</p>
          </Content>

          <CenteredForm onSubmit={this.handleDatesSelected}>
            <MultiDatePicker
              startDate={startDate}
              startDateId="report-start-date"
              endDate={endDate}
              endDateId="report-end-date"
              focusedInput={focusedInput}
              onDatesChange={this.handleDateChange}
              onFocusChange={this.handleFocusChange}
              block={false}
              showClearDates={false}
            />
            <Button type="submit" isColor="primary">
              Submit Dates
            </Button>
          </CenteredForm>
        </Box>
        <Conditional check={step > 1}>
          <ReportOptions
            startDate={startDate}
            endDate={endDate}
            handleChange={this.handleChange}
            exludeTimeMax={exludeTimeMax}
            animalType={animalType}
            setStep={this.setStep}
            handleSubmit={this.getEnrichmentData}
          />
        </Conditional>
        <Conditional check={step > 2}>
          <ReportContent
            enrichmentData={enrichmentData}
            startDate={startDate}
            endDate={endDate}
            animalType={animalType}
          />
        </Conditional>
      </Container>
    );
  }
}

export default Report;
